import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { PaymentService } from '../../payment/payment.service';
import { loadStripe } from '@stripe/stripe-js';

@Injectable()
export class CountriesService {
  list: any;
  obs: Observable<any>;
  private langEquivalences = {
    en: 'eng',
    de: 'deu',
    fr: 'fra',
    hr: 'hrv',
    it: 'ita',
    jp: 'jpn',
    nl: 'nld',
    pt: 'por',
    ru: 'rus',
    sl: 'slk',
    es: 'spa',
    fn: 'fin',
    zh: 'zho'
  };
  private countriesOptionsSubject = new BehaviorSubject({});

  constructor(private http: HttpClient, private translate: TranslateService) {
    translate.onLangChange.subscribe(() => {
      this.countriesOptionsSubject.next({ groups: this.getOptions() });
    });
  }

  getCountries(): Observable<any> | any {
    if (!this.list) {
      this.obs = this.http.get('assets/data/countries.json').pipe(
        map((list: any[]) => {
          this.list = list;
          this.countriesOptionsSubject.next({ groups: this.getOptions() });
          return this.list;
        })
      );
      return this.obs;
    }
    return this.list;
  }

  getOptions() {
    const localLanguage = this.getLocalLanguage();
    const globalList = this.list
      .map((country) => ({
        key: country.cca3,
        value:
          localLanguage && country.translations[localLanguage]
            ? country.translations[localLanguage].common
            : country.name.common
      }))
      .sort((a, b) => a.value.localeCompare(b.value));

    return globalList;
  }

  getOptionsObservable(): Observable<any> {
    return this.countriesOptionsSubject.asObservable();
  }

  getTimezoneOptions(countryCCA3) {
    const country = this.list.find((item) => item.cca3 === countryCCA3);
    if (!country) {
      return [];
    }
    return country.timezones.map((timezone) => ({ key: timezone, value: timezone }));
  }

  getCountry(countryCCA3) {
    return this.list.find((country) => country.cca3 === countryCCA3);
  }

  getCountryName(countryCCA3) {
    if (!countryCCA3) {
      return '';
    }
    const localLanguage = this.getLocalLanguage();
    const country = this.getCountry(countryCCA3);
    return localLanguage ? country.translations[localLanguage].common : country.name.common;
  }

  getLocalLanguage() {
    const lang = this.translate.currentLang || this.translate.getDefaultLang();
    return this.langEquivalences[lang];
  }

  getDefaultTimezone(country) {
    const defaults = {
      CHL: 'America/Santiago',
      DEU: 'Europe/Berlin',
      ESP: 'Europe/Madrid',
      FRA: 'Europe/Paris',
      MEX: 'America/Mexico_City',
      PRT: 'Europe/Lisbon',
      USA: 'America/New_York'
    };
    if (defaults[country]) {
      return defaults[country];
    }
    const countryTimezones = this.getTimezoneOptions(country);
    if (countryTimezones && countryTimezones.length) {
      return countryTimezones[0].key;
    }
    return null;
  }
}

export const CountriesResolver: ResolveFn<any> = (): Observable<any> | any => {
  return inject(CountriesService).getCountries();
};
